import aiProviderAdminService from '../../aiProviderAdminService';

/**
 * Supabase Logger for AI Usage
 * Logs AI provider usage to Supabase for analytics and cost monitoring
 */
class SupabaseAILogger {
  /**
   * Log AI usage to Supabase
   * @param {Object} logData - Usage data
   */
  async logUsage(logData) {
    try {
      // Calculate estimated cost based on tokens and pricing
      const estimatedCost = this.calculateCost(
        logData.usage?.totalTokens || 0,
        logData.pricing
      );

      const result = await aiProviderAdminService.logUsage({
        provider: logData.provider,
        model: logData.model,
        task: logData.task,
        success: logData.success !== false,
        duration: logData.duration,
        usage: logData.usage,
        estimatedCost,
        userId: logData.userId,
        sessionId: logData.sessionId,
        error: logData.error,
        metadata: logData.metadata || {}
      });

      if (!result.success) {
        console.warn('[Supabase AI Logger] Failed to log usage:', result.error);
      }

      return result;
    } catch (error) {
      // Don't throw - logging should not break the main flow
      console.error('[Supabase AI Logger] Error logging usage:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate estimated cost based on tokens and pricing
   */
  calculateCost(totalTokens, pricing) {
    if (!pricing || !totalTokens) return 0;

    // Simplified calculation - assumes equal distribution of input/output
    // For more accurate cost, use actual promptTokens and completionTokens
    const avgCostPerMillion = (
      (pricing.inputCostPerMillion || 0) +
      (pricing.outputCostPerMillion || 0)
    ) / 2;

    return (totalTokens / 1000000) * avgCostPerMillion;
  }

  /**
   * Log successful CV extraction
   */
  async logCVExtraction(provider, model, usage, duration) {
    return this.logUsage({
      provider,
      model,
      task: 'cv_extraction',
      success: true,
      duration,
      usage,
      metadata: { type: 'cv_extraction' }
    });
  }

  /**
   * Log successful immigration analysis
   */
  async logImmigrationAnalysis(provider, model, usage, duration) {
    return this.logUsage({
      provider,
      model,
      task: 'immigration_analysis',
      success: true,
      duration,
      usage,
      metadata: { type: 'immigration_analysis' }
    });
  }

  /**
   * Log failed operation
   */
  async logError(provider, model, task, error, duration) {
    return this.logUsage({
      provider,
      model,
      task,
      success: false,
      duration,
      error: error.message || String(error),
      metadata: { error: true }
    });
  }
}

// Export singleton
const supabaseAILogger = new SupabaseAILogger();
export default supabaseAILogger;
export { SupabaseAILogger };
